import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, PieChart, Pie, Cell } from "recharts";
import { Award, ChevronRight, FileDown, FileSpreadsheet, GraduationCap, Mail, Trophy } from "lucide-react";
import { api, descargarExcel, descargarPdf, apiError } from "@/lib/api";
import { money, fecha } from "@/lib/format";
import { usePermisos } from "@/lib/permisos";
import { Loading, PageTitle } from "@/components/Common";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const PERIODOS = ["Primer Periodo", "Segundo Periodo", "Tercer Periodo", "Cuarto Periodo"];

const COLORS = ["hsl(211 79% 42%)", "hsl(27 91% 50%)", "hsl(199 78% 55%)", "hsl(38 92% 62%)"];

const Tarjeta = ({ label, value, hint, tone = "default", tipo, onOpen, testId }) => {
  const tones = { default: "text-foreground", good: "text-primary", warn: "text-accent", bad: "text-destructive" };
  return (
    <button onClick={() => onOpen(tipo)} data-testid={testId}
            className="lift w-full rounded-lg border border-border/60 bg-card p-6 text-left">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{label}</p>
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
      </div>
      <p className={`mt-4 font-display text-2xl font-extrabold tracking-tight sm:text-3xl ${tones[tone]}`}>{value}</p>
      {hint && <p className="mt-2 text-xs text-muted-foreground">{hint}</p>}
      <p className="mt-3 text-[10px] uppercase tracking-widest text-primary">Ver detalle</p>
    </button>
  );
};

const MEDALLAS = ["bg-accent/15 text-accent border-accent/30",
                  "bg-muted text-foreground border-border",
                  "bg-primary/10 text-primary border-primary/30"];

const PodioGrado = ({ grado, conPromedio, onDiploma, onEnviar, enviando }) => (
  <div className="rounded-lg border border-border/60 bg-background p-5"
       data-testid={`podio-${grado.grado}`}>
    <div className="flex items-baseline justify-between gap-2">
      <h3 className="font-display text-sm font-bold">{grado.grado}</h3>
      <span className="text-xs text-muted-foreground">
        {grado.evaluados} evaluado(s)
      </span>
    </div>
    {grado.podio.length === 0 ? (
      <p className="mt-3 text-xs text-muted-foreground">Sin estudiantes con notas registradas.</p>
    ) : (
      <ol className="mt-3 space-y-2">
        {grado.podio.map((p) => (
          <li key={p.estudiante_id} className="flex items-center gap-2">
            <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs font-bold ${
              MEDALLAS[Math.min(p.puesto - 1, 2)]}`}>{p.puesto}</span>
            <span className="flex-1 truncate text-sm">{p.nombre}</span>
            <span className="font-mono text-sm font-semibold">{p.promedio}</span>
            <button type="button" title="Descargar diploma" onClick={() => onDiploma(p)}
                    data-testid={`diploma-${p.estudiante_id}`}
                    className="rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-accent/10 hover:text-accent">
              <Award className="h-4 w-4" />
            </button>
            <button type="button" title="Enviar el diploma a la familia" disabled={enviando}
                    onClick={() => onEnviar(p)} data-testid={`enviar-diploma-${p.estudiante_id}`}
                    className="rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary disabled:opacity-40">
              <Mail className="h-4 w-4" />
            </button>
          </li>
        ))}
      </ol>
    )}
    {grado.excluidos_morosidad > 0 && (
      <p className="mt-3 text-[11px] text-muted-foreground">
        {grado.excluidos_morosidad} excluido(s) por facturas vencidas
      </p>
    )}
    {grado.podio.length > 0 && (
      <Button size="sm" variant="ghost" className="mt-3 rounded-full px-3 text-xs"
              data-testid={`diplomas-grado-${grado.grado}`}
              onClick={() => onDiploma(null, grado.grado)}>
        <FileDown className="mr-2 h-3.5 w-3.5" /> Diplomas del grado
        {conPromedio ? "" : " (sin promedio)"}
      </Button>
    )}
  </div>
);

export default function AdminReportes() {
  const [r, setR] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [periodo, setPeriodo] = useState("");
  const [bajando, setBajando] = useState(false);
  const [ranking, setRanking] = useState(null);
  const [conPromedio, setConPromedio] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const { puede } = usePermisos();
  const puedeAcademico = puede("academico:reportes");

  useEffect(() => {
    api.get("/reportes/financiero").then((x) => setR(x.data));
    if (!puedeAcademico) return;
    api.get("/reportes/ranking-grados").then((x) => setRanking(x.data)).catch(() => setRanking({ grados: [] }));
  }, [puedeAcademico]);

  const bajarDiploma = async (est, grado) => {
    try {
      if (est) {
        await descargarPdf(`/reportes/diploma/${est.estudiante_id}`, { promedio: conPromedio },
                           `Diploma-${est.nombre}.pdf`);
      } else {
        await descargarPdf("/reportes/diplomas", { grado, promedio: conPromedio },
                           `Diplomas-${grado || "cuadro-de-honor"}.pdf`);
      }
      toast.success("Diploma descargado");
    } catch (e) {
      toast.error(apiError(e, "No se pudo generar el diploma"));
    }
  };

  const enviarDiploma = async (est) => {
    setEnviando(true);
    try {
      const { data } = await api.post(`/reportes/diploma/${est.estudiante_id}/enviar`);
      if (data.correo_enviado) {
        toast.success(`Diploma enviado a ${data.enviado_a}`);
      } else {
        toast.info(data.motivo || "Se avisó a la familia por el portal");
      }
    } catch (e) {
      toast.error(apiError(e, "No se pudo enviar el diploma"));
    } finally {
      setEnviando(false);
    }
  };

  const abrir = async (tipo) => {
    setDetalle({ cargando: true });
    const { data } = await api.get("/reportes/detalle", { params: { tipo } });
    setDetalle(data);
  };

  if (!r) return <Loading />;

  return (
    <div data-testid="admin-reportes">
      <PageTitle title="Reportes financieros"
                 subtitle="Toque cualquier indicador para ver su detalle: facturas, gastos o ventas que lo componen." />

      <section className={`mb-8 rounded-lg border border-border/60 bg-card p-6 sm:p-8 ${puedeAcademico ? "" : "hidden"}`}
               data-testid="panel-boletin-general">
        <div className="flex items-center gap-2 text-primary">
          <GraduationCap className="h-4 w-4" />
          <h2 className="font-display text-lg font-bold">Boletín general del colegio</h2>
        </div>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Excel con una hoja por grado (estudiantes, notas por materia, promedio y condición) y una
          hoja resumen con los promedios, aprobados y reprobados de cada grado. Útil para el informe
          del MINERD.
        </p>
        <div className="mt-5 flex flex-wrap items-end gap-4">
          <div className="space-y-2">
            <label className="text-xs uppercase tracking-widest text-muted-foreground">Periodo</label>
            <select value={periodo} onChange={(e) => setPeriodo(e.target.value)}
                    data-testid="periodo-boletin-general"
                    className="block rounded-md border border-border bg-background px-3 py-2 text-sm">
              <option value="">Promedio general del año</option>
              {PERIODOS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <Button className="rounded-full" disabled={bajando} data-testid="descargar-boletin-general"
                  onClick={async () => {
                    setBajando(true);
                    try {
                      await descargarExcel("/reportes/boletin-general/excel", { periodo },
                        `boletin-general-${(periodo || "anual").toLowerCase().replace(/ /g, "-")}-${new Date().toISOString().slice(0, 10)}.xlsx`);
                      toast.success("Boletín general descargado");
                    } catch (e) {
                      toast.error("No se pudo generar el boletín general");
                    }
                    setBajando(false);
                  }}>
            <FileSpreadsheet className="mr-2 h-4 w-4" />
            {bajando ? "Generando…" : "Descargar boletín general (Excel)"}
          </Button>
        </div>
      </section>

      <section className={`mb-8 rounded-lg border border-border/60 bg-card p-6 sm:p-8 ${puedeAcademico ? "" : "hidden"}`}
               data-testid="panel-ranking-grados">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-accent">
              <Trophy className="h-4 w-4" />
              <h2 className="font-display text-lg font-bold">Cuadro de honor por grado</h2>
            </div>
            <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
              Los tres mejores promedios generales del año de cada grado. No entran los estudiantes
              con facturas vencidas.
            </p>
          </div>
          <Button variant="outline" className="rounded-full" data-testid="descargar-ranking-grados"
                  onClick={async () => {
                    try {
                      await descargarExcel("/reportes/ranking-grados/excel", {},
                        `cuadro-de-honor-${new Date().toISOString().slice(0, 10)}.xlsx`);
                      toast.success("Cuadro de honor descargado");
                    } catch (e) {
                      toast.error("No se pudo generar el cuadro de honor");
                    }
                  }}>
            <FileSpreadsheet className="mr-2 h-4 w-4" /> Descargar Excel
          </Button>
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-4">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
            <input type="checkbox" checked={conPromedio} data-testid="diploma-con-promedio"
                   onChange={(e) => setConPromedio(e.target.checked)}
                   className="h-4 w-4 rounded border-border accent-primary" />
            Mostrar el promedio en los diplomas
          </label>
          <Button variant="outline" size="sm" className="rounded-full"
                  data-testid="descargar-todos-diplomas"
                  onClick={() => bajarDiploma(null, undefined)}>
            <Award className="mr-2 h-4 w-4" /> Descargar todos los diplomas
          </Button>
        </div>
        {!ranking ? <Loading label="Calculando promedios…" /> : (
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="grid-ranking">
            {(ranking.grados || []).filter((g) => g.podio.length > 0)
              .map((g) => <PodioGrado key={g.grado} grado={g} conPromedio={conPromedio}
                                      onDiploma={bajarDiploma} onEnviar={enviarDiploma}
                                      enviando={enviando} />)}
            {(ranking.grados || []).filter((g) => g.podio.length > 0).length === 0 && (
              <p className="text-sm text-muted-foreground" data-testid="ranking-vacio">
                Todavía no hay calificaciones registradas para armar el cuadro de honor.
              </p>
            )}
          </div>
        )}
      </section>

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <Tarjeta testId="rep-ingresos" label="Ingresos totales" value={money(r.ingresos_totales)} tone="good"
                 tipo="ingresos" onOpen={abrir} hint="Facturas cobradas" />
        <Tarjeta testId="rep-cxc" label="Cuentas por cobrar" value={money(r.cuentas_por_cobrar)} tone="warn"
                 tipo="cuentas_por_cobrar" onOpen={abrir} hint="Facturas pendientes" />
        <Tarjeta testId="rep-morosidad" label="Morosidad" value={money(r.morosidad)} tone="bad"
                 tipo="morosidad" onOpen={abrir} hint="Vencidas sin pagar" />
        <Tarjeta testId="rep-caja" label="Cierre de caja (hoy)" value={money(r.cierre_caja_hoy.monto)}
                 tipo="caja_hoy" onOpen={abrir} hint={`${r.cierre_caja_hoy.cantidad} transacción(es)`} />
      </div>

      <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <Tarjeta testId="rep-descuentos" label="Descuentos otorgados" value={money(r.descuentos_aplicados)}
                 tipo="descuentos" onOpen={abrir} hint="Becas y promociones" />
        <Tarjeta testId="rep-gastos" label="Gastos del colegio" value="Ver detalle" tone="bad"
                 tipo="gastos" onOpen={abrir} hint="Fijos y diarios" />
        <Tarjeta testId="rep-ventas" label="Ventas" value="Ver detalle" tipo="ventas" onOpen={abrir}
                 hint="Uniformes, libros y útiles" />
        <div className="rounded-lg border border-border/60 bg-card p-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Estudiantes activos</p>
          <p className="mt-4 font-display text-2xl font-extrabold sm:text-3xl">{r.total_estudiantes}</p>
          <p className="mt-2 text-xs text-muted-foreground">
            {r.total_facturas} facturas emitidas
            {r.sin_valor_fiscal?.cantidad ? (
              <span data-testid="rep-sin-valor-fiscal">
                {" "}· {r.sin_valor_fiscal.cantidad} sin valor fiscal ({money(r.sin_valor_fiscal.monto)})
              </span>
            ) : null}
          </p>
        </div>
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-12">
        <div className="lg:col-span-7 rounded-lg border border-border/60 bg-card p-6 sm:p-8">
          <h2 className="font-display text-xl font-bold tracking-tight">Ingresos cobrados por mes</h2>
          <div className="mt-6 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={r.por_mes}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(213 28% 88%)" vertical={false} />
                <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${v / 1000}k`} />
                <Tooltip formatter={(v) => money(v)} />
                <Bar dataKey="monto" fill="hsl(211 79% 42%)" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="lg:col-span-5 rounded-lg border border-border/60 bg-card p-6 sm:p-8">
          <h2 className="font-display text-xl font-bold tracking-tight">Ingresos por concepto</h2>
          <div className="mt-6 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={r.por_concepto} dataKey="monto" nameKey="concepto" innerRadius={60} outerRadius={100}>
                  {r.por_concepto.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => money(v)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 space-y-2">
            {r.por_concepto.map((c, i) => (
              <div key={c.concepto} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 capitalize">
                  <span className="h-2 w-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                  {c.concepto}
                </span>
                <span className="font-mono">{money(c.monto)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Dialog open={!!detalle} onOpenChange={(o) => !o && setDetalle(null)}>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto" data-testid="modal-detalle-reporte">
          <DialogHeader>
            <DialogTitle className="font-display">{detalle?.titulo || "Detalle"}</DialogTitle>
            <DialogDescription>
              {detalle?.items ? `${detalle.items.length} registro(s) · Total ${money(detalle.total)}` : "Cargando…"}
            </DialogDescription>
          </DialogHeader>
          {detalle?.items && (
            <Button variant="outline" size="sm" className="w-fit rounded-full"
                    data-testid="descargar-excel-reporte"
                    onClick={() => descargarExcel("/reportes/excel", { tipo: detalle.tipo },
                                                  `${detalle.tipo.replace(/_/g, "-")}-${new Date().toISOString().slice(0, 10)}.xlsx`)}>
              <FileSpreadsheet className="mr-2 h-4 w-4" /> Descargar Excel
            </Button>
          )}
          {detalle?.items && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="tabla-detalle-reporte">
                <thead className="bg-muted/50 text-left text-xs uppercase tracking-widest text-muted-foreground">
                  {detalle.columnas === "facturas" ? (
                    <tr>
                      <th className="px-4 py-3">NCF</th>
                      <th className="px-4 py-3">Estudiante</th>
                      <th className="px-4 py-3">Concepto</th>
                      <th className="px-4 py-3">Fecha</th>
                      <th className="px-4 py-3">Monto</th>
                    </tr>
                  ) : detalle.columnas === "gastos" ? (
                    <tr>
                      <th className="px-4 py-3">Fecha</th>
                      <th className="px-4 py-3">Tipo</th>
                      <th className="px-4 py-3">Categoría</th>
                      <th className="px-4 py-3">Descripción</th>
                      <th className="px-4 py-3">Monto</th>
                    </tr>
                  ) : (
                    <tr>
                      <th className="px-4 py-3">Fecha</th>
                      <th className="px-4 py-3">Categoría</th>
                      <th className="px-4 py-3">Descripción</th>
                      <th className="px-4 py-3">Estudiante</th>
                      <th className="px-4 py-3">Total</th>
                    </tr>
                  )}
                </thead>
                <tbody>
                  {detalle.items.map((it) => (
                    <tr key={it.id} className="border-t border-border/60">
                      {detalle.columnas === "facturas" ? (
                        <>
                          <td className="px-4 py-3 font-mono text-xs">{it.ncf}</td>
                          <td className="px-4 py-3">{it.estudiante_nombre}</td>
                          <td className="px-4 py-3">{it.concepto}</td>
                          <td className="px-4 py-3 font-mono text-xs">
                            {fecha(it.fecha_pago || it.fecha_vencimiento)}
                          </td>
                          <td className="px-4 py-3 font-mono font-semibold">
                            {money(detalle.tipo === "descuentos" ? it.monto_descuento : it.monto_total)}
                          </td>
                        </>
                      ) : detalle.columnas === "gastos" ? (
                        <>
                          <td className="px-4 py-3 font-mono text-xs">{fecha(it.fecha)}</td>
                          <td className="px-4 py-3"><Badge variant="outline">{it.tipo}</Badge></td>
                          <td className="px-4 py-3">{it.categoria}</td>
                          <td className="px-4 py-3">{it.descripcion}</td>
                          <td className="px-4 py-3 font-mono font-semibold text-destructive">-{money(it.monto)}</td>
                        </>
                      ) : (
                        <>
                          <td className="px-4 py-3 font-mono text-xs">{fecha(it.fecha)}</td>
                          <td className="px-4 py-3">{it.categoria}</td>
                          <td className="px-4 py-3">{it.descripcion}</td>
                          <td className="px-4 py-3 text-muted-foreground">{it.estudiante_nombre || "—"}</td>
                          <td className="px-4 py-3 font-mono font-semibold">{money(it.total)}</td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              {detalle.items.length === 0 && (
                <p className="p-8 text-center text-sm text-muted-foreground" data-testid="detalle-vacio">
                  No hay registros en este reporte.
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
